import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtMonth, fmtNum } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ExportButton from "@/components/common/ExportButton";
import MultiSelectFilter from "@/components/common/MultiSelectFilter";
import { useCustomization } from "@/context/CustomizationContext";
import { toast } from "sonner";
import { Pencil, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const MONTH_OPTIONS = [
  { value: "01", label: "January" }, { value: "02", label: "February" }, { value: "03", label: "March" },
  { value: "04", label: "April" }, { value: "05", label: "May" }, { value: "06", label: "June" },
  { value: "07", label: "July" }, { value: "08", label: "August" }, { value: "09", label: "September" },
  { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
];

const DEFAULT_GROUPS = [
  { key: "total", labelKey: "total", tint: "#f8fafc", headTint: "#f1f5f9" },
  { key: "ar", labelKey: "ar", tint: "#f0fdfa", headTint: "#ccfbf1" },
  { key: "coding", labelKey: "coding", tint: "#f0f9ff", headTint: "#e0f2fe" },
  { key: "combined_rcm_coding", labelKey: "combined", tint: "#ecfdf5", headTint: "#d1fae5" },
  { key: "payment_posting", labelKey: "payment_posting", tint: "#fffbeb", headTint: "#fef3c7" },
  { key: "patient_calling", labelKey: "patient_calling", tint: "#fff7ed", headTint: "#ffedd5" },
  { key: "pre_auth", labelKey: "pre_auth", tint: "#ecfeff", headTint: "#cffafe" },
];
const DEFAULT_CATEGORY_LABELS = {
  total: "Total", ar: "AR", coding: "Coding", combined: "Combined RCM/Coding",
  payment_posting: "Payment Posting", patient_calling: "Patient Calling", pre_auth: "Pre-Authorization",
};
const DEFAULT_METRIC_LABELS = { invoice: "Invoice", prorated: "Prorated FTE", billable: "Billable FTE" };

const DEFAULT_METRICS = [
  { key: "invoice", label: "Invoice", type: "currency", financial: true },
  { key: "prorated", label: "Prorated FTE", type: "number", financial: false },
  { key: "billable", label: "Billable FTE", type: "number", financial: false },
];
const HEADER_ROW1_H = 34; // px - height of the first header row (used for freeze offset)

export default function RevenueSummaryPage() {
  const { user } = useAuth();
  const { configs, summaryLabels, sectionLabels } = useCustomization();
  const summaryTitle = sectionLabels?.revenue_summary || "Revenue Summary";
  const detailsTitle = sectionLabels?.revenue_details || "Revenue Details";
  const canEdit = Boolean(user?.role);
  const canViewFinancial = ["super_admin", "cxo", "strategy"].includes(user?.role);

  const summaryConfig = configs?.revenue_summary?.config || {
    categories: DEFAULT_GROUPS.slice(1).map((group) => ({
      key: group.key, label: DEFAULT_CATEGORY_LABELS[group.labelKey] || group.key,
    })),
    metrics: DEFAULT_METRICS,
  };
  const groups = [
    { ...DEFAULT_GROUPS[0], label: summaryLabels?.categories?.total || "Total" },
    ...summaryConfig.categories.map((category, index) => ({
      key: category.key,
      label: category.label,
      tint: DEFAULT_GROUPS[(index + 1) % DEFAULT_GROUPS.length].tint,
      headTint: DEFAULT_GROUPS[(index + 1) % DEFAULT_GROUPS.length].headTint,
    })),
  ];
  const metrics = summaryConfig.metrics?.length ? summaryConfig.metrics : DEFAULT_METRICS;
  const metricByKey = Object.fromEntries(metrics.map((metric) => [metric.key, metric]));
  const metricKeys = metrics.map((metric) => metric.key);

  const catLabel = (groupKey) => {
    return groups.find((group) => group.key === groupKey)?.label ||
      summaryLabels?.categories?.[groupKey] || DEFAULT_CATEGORY_LABELS[groupKey] || groupKey;
  };
  const metricLabel = (m) => metricByKey[m]?.label || summaryLabels?.metrics?.[m] || DEFAULT_METRIC_LABELS[m] || m;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [monthsFilter, setMonthsFilter] = useState([]); // ["01".."12"]
  const [yearsFilter, setYearsFilter] = useState([]);   // ["2025", "2026"]
  const [sortField, setSortField] = useState("month");
  const [sortDir, setSortDir] = useState("desc");        // latest month on top by default
  const [cellEdit, setCellEdit] = useState(null); // {month, category, metric, value, isOverridden, autoValue}
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/summary")
      .then((res) => {
        setRows(res.data.rows);
      })
      .catch(() => toast.error("Failed to load revenue summary"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  // Always reflect the latest detail-section data: refresh when returning to this tab/window
  useEffect(() => {
    const onFocus = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCellEdit = (row, category, metric) => {
    if (metricByKey[metric]?.financial && !canViewFinancial) return; // financial values restricted
    const key = `${category}.${metric}`;
    const isOverridden = (row.overridden || []).includes(key);
    setCellEdit({
      month: row.month,
      category,
      metric,
      value: row[category][metric],
      isOverridden,
      autoValue: isOverridden ? row.computed?.[key] : row[category][metric],
    });
  };

  const saveCellOverride = async () => {
    setSaving(true);
    try {
      await api.put("/summary-override", {
        month: cellEdit.month,
        category: cellEdit.category,
        metric: cellEdit.metric,
        value: parseFloat(cellEdit.value) || 0,
      });
      toast.success(`${catLabel(cellEdit.category)} ${metricLabel(cellEdit.metric)} updated for ${fmtMonth(cellEdit.month)}`);
      setCellEdit(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save value"));
    } finally {
      setSaving(false);
    }
  };

  const resetCellOverride = async () => {
    setSaving(true);
    try {
      await api.delete("/summary-override", {
        params: { month: cellEdit.month, category: cellEdit.category, metric: cellEdit.metric },
      });
      toast.success("Reverted to auto-calculated value");
      setCellEdit(null);
      load();
    } catch {
      toast.error("Failed to reset value");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-[600px] rounded-xl" data-testid="summary-loading" />;

  const baseVisible = hideEmpty ? rows.filter((r) => r.has_data ?? (r.total.invoice > 0)) : rows;
  const yearOptions = [...new Set(baseVisible.map((r) => r.month.slice(0, 4)))].sort();
  let visible = baseVisible.filter((r) =>
    (monthsFilter.length === 0 || monthsFilter.includes(r.month.slice(5, 7))) &&
    (yearsFilter.length === 0 || yearsFilter.includes(r.month.slice(0, 4))));
  visible = [...visible].sort((a, b) => {
    const cmp = a.month.localeCompare(b.month);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortIcon = (field) => (
    sortField === field
      ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
      : <ArrowUpDown className="h-3 w-3 opacity-30" />
  );

  // grand totals (effective values)
  const grand = {};
  groups.forEach(({ key }) => {
    grand[key] = visible.reduce(
      (acc, r) => metricKeys.reduce((next, metric) => ({
        ...next, [metric]: next[metric] + (r[key]?.[metric] || 0),
      }), acc),
      Object.fromEntries(metricKeys.map((metric) => [metric, 0]))
    );
  });

  const fmtCell = (metric, v) => {
    if (metricByKey[metric]?.type === "percentage") {
      return !canViewFinancial || v == null ? <span className="text-slate-400">%</span> : `${fmtNum(v)}%`;
    }
    if (!metricByKey[metric]?.financial) return fmtNum(v);
    if (!canViewFinancial || v == null) return <span className="text-slate-400">$</span>; // masked
    return v > 0 ? fmtCurrency(v) : "-";
  };

  return (
    <div className="space-y-4" data-testid="revenue-summary-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-600">
            Auto-calculated from {detailsTitle} · <span className="font-medium">{visible.length} months</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Click any category value to edit it manually — the TOTAL columns always show the sum. Edited cells are marked
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] mx-1 align-middle" />and can be reset to auto.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MultiSelectFilter label="Month" testId="summary-month-filter" className="w-[140px]"
            options={MONTH_OPTIONS} selected={monthsFilter} onChange={setMonthsFilter} searchable={false} />
          <MultiSelectFilter label="Year" testId="summary-year-filter" className="w-[110px]"
            options={yearOptions} selected={yearsFilter} onChange={setYearsFilter} searchable={false} />
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="summary-toggle-empty-months"
            onClick={() => setHideEmpty(!hideEmpty)}>
            {hideEmpty ? "Show future months" : "Hide empty months"}
          </Button>
          <ExportButton testId="revenue-summary-export-button" />
        </div>
      </div>

      <div className="rounded-xl border bg-white shadow-[var(--app-shadow-sm)] overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-15rem)] overflow-y-auto">
          <table className="sticky-table w-full text-xs border-collapse" data-testid="revenue-summary-table">
            <thead>
              {/* Header row 1 - frozen at top:0 */}
              <tr>
                <th rowSpan={2}
                  className="sticky-col border-b border-r px-1 text-left font-semibold text-slate-700 min-w-[110px]">
                  <button data-testid="summary-sort-month" onClick={() => toggleSort("month")}
                    title="Sort by month & year"
                    className={`inline-flex items-center gap-1 rounded px-2 py-1.5 hover:bg-slate-100 transition-[background-color] duration-150 ${sortField === "month" ? "text-[hsl(var(--primary))]" : ""}`}>
                    Month &amp; Year {sortIcon("month")}
                  </button>
                </th>
                {groups.map((g) => (
                  <th key={g.key} colSpan={metrics.length}
                    style={{ background: g.headTint, height: HEADER_ROW1_H }}
                    className="border-b border-r px-2 text-center font-bold text-slate-700 uppercase">
                    {catLabel(g.key)}
                  </th>
                ))}
              </tr>
              {/* Header row 2 - frozen right below row 1 */}
              <tr>
                {groups.map((g) => (
                  metricKeys.map((m) => (
                    <th key={`${g.key}-${m}`}
                      style={{ background: g.headTint, top: HEADER_ROW1_H }}
                      className="border-b border-r px-2 py-1.5 text-right font-medium text-slate-600 whitespace-nowrap">
                      {metricLabel(m)}
                    </th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.month} className="hover:bg-slate-50 transition-[background-color] duration-150" data-testid={`summary-row-${r.month}`}>
                  <td className="sticky-col border-b border-r px-3 py-1 font-medium text-slate-800 whitespace-nowrap">
                    {fmtMonth(r.month)}
                  </td>
                  {groups.map((g) =>
                    metricKeys.map((metric) => {
                      const cellKey = `${g.key}.${metric}`;
                      const isOverridden = (r.overridden || []).includes(cellKey);
                      const value = r[g.key][metric];
                      if (g.key === "total") {
                        return (
                          <td key={cellKey} style={{ background: "#f8fafc" }}
                            className="border-b border-r px-2 py-1 text-right tabular-nums font-semibold text-slate-800">
                            {fmtCell(metric, value)}
                          </td>
                        );
                      }
                      return (
                        <td key={cellKey} style={{ background: g.tint }} className="border-b border-r p-0">
                          {canEdit ? (
                            <button
                              data-testid={`summary-cell-${r.month}-${g.key}-${metric}`}
                              onClick={() => openCellEdit(r, g.key, metric)}
                              title={isOverridden ? "Manually edited — click to change or reset" : "Click to edit"}
                              className="w-full h-full px-2 py-1 text-right tabular-nums hover:bg-teal-100/60 group flex items-center justify-end gap-1 transition-[background-color] duration-150"
                            >
                              {isOverridden && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] shrink-0" />}
                              <span className={metricByKey[metric]?.financial ? "font-medium" : ""}>{fmtCell(metric, value)}</span>
                              <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-50 shrink-0" />
                            </button>
                          ) : (
                            <div data-testid={`summary-cell-${r.month}-${g.key}-${metric}`}
                              className="w-full h-full px-2 py-1 text-right tabular-nums flex items-center justify-end gap-1">
                              {isOverridden && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] shrink-0" />}
                              <span className={metricByKey[metric]?.financial ? "font-medium" : ""}>{fmtCell(metric, value)}</span>
                            </div>
                          )}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
              {/* Grand total row */}
              <tr className="bg-slate-100 font-semibold" data-testid="summary-grand-total-row">
                <td className="sticky-col border-t-2 border-r px-3 py-2 text-slate-900" style={{ background: "#f1f5f9" }}>
                  Grand Total
                </td>
                {groups.map((g) =>
                  metricKeys.map((metric) => (
                    <td key={`gt-${g.key}-${metric}`} className="border-t-2 border-r px-2 py-2 text-right tabular-nums">
                      {metricByKey[metric]?.financial
                        ? (canViewFinancial ? fmtCurrency(grand[g.key][metric]) : <span className="text-slate-400">$</span>)
                        : fmtNum(grand[g.key][metric])}
                    </td>
                  ))
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary cell edit dialog */}
      <Dialog open={!!cellEdit} onOpenChange={(o) => !o && setCellEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Summary Value</DialogTitle>
            <DialogDescription>
              {cellEdit && `${fmtMonth(cellEdit.month)} · ${catLabel(cellEdit.category)} · ${metricLabel(cellEdit.metric)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{cellEdit && metricLabel(cellEdit.metric)}</Label>
              <Input type="number" step="0.01" data-testid="summary-cell-input" className="text-right tabular-nums"
                value={cellEdit?.value ?? ""}
                onChange={(e) => setCellEdit({ ...cellEdit, value: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && saveCellOverride()} />
            </div>
            <div className="rounded-lg bg-slate-50 border p-3 text-xs text-slate-500 space-y-1">
              <p>
                Auto-calculated value:{" "}
                <span className="font-semibold text-slate-700 tabular-nums">
                  {cellEdit && (metricByKey[cellEdit.metric]?.financial ? fmtCurrency(cellEdit.autoValue || 0) : fmtNum(cellEdit.autoValue) || "0")}
                </span>
              </p>
              <p>Saving a manual value overrides the auto calculation for this cell. The TOTAL column is always re-summed.</p>
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            {cellEdit?.isOverridden ? (
              <Button variant="outline" data-testid="summary-cell-reset-button" onClick={resetCellOverride}
                disabled={saving} className="text-slate-600">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to auto
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCellEdit(null)}>Cancel</Button>
              <Button data-testid="summary-cell-save-button" onClick={saveCellOverride} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
